/**
 * Colour for a B2B tracking status.
 *
 * Renders the same solid badge the Orders tab uses (`orderStatusBadge` +
 * `status*` in ManageOrders.css), so one status reads the same across the
 * terminal.
 *
 * Why a B2B-specific table rather than calling `getOrderStatusClass`
 * -----------------------------------------------------------------
 * That helper keys off `OrderStatus` — new, confirmed, ready_to_ship,
 * in_transit, delivered, exceptions, rto, held, cancelled, lost, damaged. B2B
 * does not store those: `B2BShipment.progress` and `B2BOrder.derived_status`
 * carry `ShipmentProgress`, a 28-value courier vocabulary whose members
 * `picked_up`, `out_for_pickup`, `out_for_delivery`, `manifested`,
 * `awb_registered` and the whole `rto_*` family have no Orders-tab counterpart.
 * Passing one to `getOrderStatusClass` would fall through to `statusDefault`
 * and paint it blue — confidently wrong, which is worse than uncoloured.
 *
 * So every value is placed deliberately below, and anything unrecognised stays
 * grey rather than borrowing a colour that implies we understood it.
 */

const CLASSES = {
  // Booked, nothing has moved yet.
  order_placed: 'statusNewOrders',
  awb_registered: 'statusNewOrders',
  awb_registered_on_proship: 'statusNewOrders',
  manifested: 'statusNewOrders',
  pickup_pending: 'statusNewOrders',
  out_for_pickup: 'statusNewOrders',

  // The courier has it and nothing has gone wrong — the nearest honest reading
  // of the Orders tab's green "confirmed".
  picked_up: 'statusConfirmed',

  // Moving.
  in_transit: 'statusInTransit',
  out_for_delivery: 'statusInTransit',

  // Arrived. `rto_delivered` is green rather than red because the journey
  // resolved — the goods are safely back, not missing.
  delivered: 'statusDelivered',
  rto_delivered: 'statusDelivered',

  // Needs a human, but not yet a loss.
  pickup_failed: 'statusHeld',
  failed_delivery: 'statusHeld',
  not_serviceable: 'statusHeld',
  shipment_delayed: 'statusHeld',
  shipment_held: 'statusHeld',
  contact_customer_care: 'statusHeld',
  rto_shipment_delay: 'statusHeld',
  rto_contact_customer_care: 'statusHeld',

  // Coming back.
  rto_requested: 'statusRTO',
  rto: 'statusRTO',
  rto_in_transit: 'statusRTO',
  rto_out_for_delivery: 'statusRTO',
  rto_failed: 'statusRTO',

  // Went wrong.
  lost: 'statusLost',
  damaged: 'statusDamaged',
  cancelled_order: 'statusCancelled',
  cancelled_pending: 'statusCancelled',
};

/**
 * Badge class for one status value.
 *
 * A blank value is a real state here, not missing data: it means no consignment
 * has been booked yet, which reads grey.
 */
export function b2bStatusClass(progress) {
  if (!progress) return 'statusReadyToShip';
  return CLASSES[progress] || 'statusReadyToShip';
}

/** Badge class for a list row or detail header. */
export function b2bOrderStatusClass(order) {
  if (!order) return 'statusReadyToShip';
  // A deleted order outranks whatever its shipments last said.
  if (order.is_deleted) return 'statusCancelled';
  return b2bStatusClass(order.derived_status);
}

export default b2bStatusClass;
